/**
 * Pagination utilities for Comments component
 * Handles pagination logic and page navigation
 */

/**
 * Gets the current page of comments based on pagination settings
 * @param {Array} comments - Array of all comments
 * @param {number} currentPage - Current page number
 * @param {number} commentsPerPage - Number of comments per page
 * @param {string} sortBy - Sorting criteria
 * @returns {Array} Array of comments for the current page
 */
export const getCurrentComments = (comments, currentPage, commentsPerPage, sortBy, sortCommentsFn) => {
  const sortedComments = sortCommentsFn(comments, sortBy);
  const indexOfLastComment = currentPage * commentsPerPage;
  const indexOfFirstComment = indexOfLastComment - commentsPerPage;
  return sortedComments.slice(indexOfFirstComment, indexOfLastComment);
};

/**
 * Calculates the total number of pages needed
 * @param {number} totalComments - Total number of comments
 * @param {number} commentsPerPage - Number of comments per page
 * @returns {number} Total number of pages
 */
export const calculateTotalPages = (totalComments, commentsPerPage) => {
  return Math.ceil(totalComments / commentsPerPage);
};

/**
 * Generates an array of page numbers to display in pagination
 * @param {number} currentPage - Current page number
 * @param {number} totalPages - Total number of pages
 * @param {number} maxVisiblePages - Maximum number of page buttons to show
 * @returns {Array} Array of page numbers to display
 */
export const generateVisiblePageNumbers = (currentPage, totalPages, maxVisiblePages = 5) => {
  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (currentPage <= 3) {
    return Array.from({ length: maxVisiblePages }, (_, i) => i + 1);
  }

  if (currentPage >= totalPages - 2) {
    return Array.from({ length: maxVisiblePages }, (_, i) => totalPages - maxVisiblePages + i + 1);
  }

  return Array.from({ length: maxVisiblePages }, (_, i) => currentPage - 2 + i);
};

/**
 * Calculates the range of comments being displayed
 * @param {number} currentPage - Current page number
 * @param {number} commentsPerPage - Number of comments per page
 * @param {number} totalComments - Total number of comments
 * @returns {Object} Object with start and end comment indices
 */
export const getCommentRange = (currentPage, commentsPerPage, totalComments) => {
  const start = ((currentPage - 1) * commentsPerPage) + 1;
  const end = Math.min(currentPage * commentsPerPage, totalComments);
  return { start, end };
};

/**
 * Checks if a page navigation action is valid
 * @param {number} targetPage - Page number to navigate to
 * @param {number} totalPages - Total number of pages
 * @returns {boolean} True if navigation is valid
 */
export const isValidPageNavigation = (targetPage, totalPages) => {
  return targetPage >= 1 && targetPage <= totalPages;
};

/**
 * Scrolls to the top of the page with smooth behavior
 */
export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};
